import { Link } from "react-router-dom";
import Block from "../components/Block";
import Seam from "../components/Seam";
import Seo from "../components/Seo";
import { SITE } from "../seo/siteConfig";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE.url}/#website`,
  url: SITE.url,
  name: SITE.name,
  inLanguage: "en",
};

export default function Home() {
  return (
    <>
      <Seo jsonLd={homeJsonLd} />
      <section className="wrap opener">
        <h1 className="hero-reveal">Most problems don't need another deck.</h1>
        <p className="lede hero-reveal hero-reveal-2">
          They need someone to understand the mess properly, then build the thing
          that fixes it.
        </p>
        <p className="body-copy hero-reveal hero-reveal-2">
          Strategy that ends in a slide is just an opinion with better formatting.
          We stay on past the thinking and into the making. Sometimes that's a
          system, sometimes a process nobody had written down, sometimes a product
          that didn't exist last quarter.
        </p>
        <div className="actions hero-reveal hero-reveal-3">
          <Link className="btn btn-solid" to="/contact">Get in touch</Link>
          <Link className="btn btn-ghost" to="/what-we-do">See what we do</Link>
        </div>
      </section>

      <Seam />

      <Block marker="What we do" bodyClassName="wide">
        <h2>From the first question to long after launch</h2>
        <ol className="stages compact">
          <li><div><h3>Understand</h3><p>Who's this for, what's actually broken, and is it worth fixing?</p></div></li>
          <li><div><h3>Shape</h3><p>Turn the problem into something buildable, and pick the right tools, AI included or not.</p></div></li>
          <li><div><h3>Build</h3><p>Build it, or run the people who do. One point of contact either way.</p></div></li>
          <li><div><h3>Run</h3><p>Keep it working after launch, and help your people actually use it.</p></div></li>
        </ol>
        <p className="small" style={{ marginTop: "2rem" }}>
          <Link to="/what-we-do">How each stage works</Link>
        </p>
      </Block>

      <Seam />

      <Block marker="Our IP & products">
        <h2>Some of what we build, we keep</h2>
        <p>
          Not everything we make is for someone else. Some of it started as a
          business idea, some as a personal itch, and some because we saw a problem
          up close and decided to solve it properly. We own all of it, and we run it
          the way we'd want a client's product run.
        </p>
        <ul className="shelf">
          <li><span>Products</span><span>Prism Books, SkillNest, The Riding Post</span></li>
          <li><span>Government systems</span><span>SAMADHAN, ACB Application Processing</span></li>
        </ul>
        <p className="small" style={{ marginTop: "1.5rem" }}>
          <Link to="/our-ip">See all of it</Link>
        </p>
      </Block>

      <Seam />

      <Block marker="How we work">
        <div className="principles">
          <div>
            <h3>We start by asking better questions</h3>
            <p>
              Most briefs describe a symptom. We'd rather find the cause first, even
              if that means telling you the project you asked for isn't the one you need.
            </p>
          </div>
          <div>
            <h3>We build small and show you early</h3>
            <p>You'll see something working before you see an invoice you weren't expecting.</p>
          </div>
          <div>
            <h3>We stay in the room</h3>
            <p>We don't hand over a repository and disappear. Whoever built it is who you talk to.</p>
          </div>
          <div>
            <h3>We tell you when we're the wrong fit</h3>
            <p>It happens. It's cheaper for everyone if we say so in week one.</p>
          </div>
        </div>
      </Block>

      <Seam />

      <Block marker="The size of us">
        <h2>We're a small team, and that's the point</h2>
        <p>
          You won't get a pitch team and then a delivery team you've never met. The
          people who show up to the first conversation are the people who build it.
        </p>
        <p>
          That limits how much we take on, which is fine. We'd rather do a few things
          properly than many things adequately.
        </p>
      </Block>

      <Seam />

      <Block marker="Next">
        <h2>Got a gap?</h2>
        <p>
          Tell us what's not working. If we can help, we'll say how. If we can't,
          we'll usually know someone who can.
        </p>
        <div className="actions">
          <Link className="btn btn-solid" to="/contact">Start a conversation</Link>
        </div>
      </Block>
    </>
  );
}
